<template>
  <div class="cashflow-app">
    <div class="bars-container">
      <months-navigation-bar :disabled="blocked"/>
      <cashflow-app-snack-bar/>
    </div>
    <subscription-mbg-timer-small v-if="hasMoneyBackOffer" :class="{ aboveCarousel: showCarousel || showNotification }" />
    <div v-if="showNotification || showCarousel">
      <notification v-if="showNotification" class="notification-wrapper"/>
      <insights-carousel :insights="filteredInsights" v-else/>
      <separator/>
    </div>
    <div class="cashflow-container">
      <cashflow-view/>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import BaseUI from 'base-ui';
import _ from 'lodash';
import MidMonthEvent
  from '@/pages/responsive-pages/authenticated/navigation-pages/cashflow-app/cashflow-view/midmonth/MidMonthEvent';
import CashflowAppSnackBar
  from '@/pages/responsive-pages/authenticated/navigation-pages/cashflow-app/CashflowAppSnackBar';
import SubscriptionMbgTimerSmall from '@/pages/responsive-pages/authenticated/trial/money-back-offer/SubscriptionMbgTimerSmall';
import Intercom from '@/Intercom';
import MoneyBackPromotions from '@/mixins/MoneyBackPromotion';
import Segment from '../../../../../Segment';
import CashflowView from './cashflow-view/CashflowView.vue';
import Notification from './notification/Notification.vue';
import InsightsCarousel from './notification/notification-types/InsightsCarousel.vue';
import casfhlowHistory from '../../../../../store/utilities/cashflow-history';
import MonthsNavigationBar from './cashflow-view/MonthsNavigationBar';
import ExpenseSummary from './cashflow-view/expense-summary/ExpenseSummary';

export default {
  name: 'CashflowApp',
  components: {
    CashflowView,
    Notification,
    InsightsCarousel,
    MonthsNavigationBar,
    CashflowAppSnackBar,
    Separator: BaseUI.Separator,
    SubscriptionMbgTimerSmall,
  },
  data() {
    return {
      showToaster: false,
      currentToasterComponent: null,
      toasterProps: null,
    };
  },
  mixins: [MoneyBackPromotions],
  async created() {
    const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
    const { userAgent } = navigator;
    Segment.trackUserGot('CashflowViewEntered', { cashflowApp: true, userAgent, vw, vh });
    this.startContentLoading(this.$options.name);
    await this.fetchMergeQuestions();
    await Promise.all([
      this.initBudgets({ budgetDate: this.budgetDate }),
      this.initApplicationState(),
      this.initInsights(),
    ]);
    if (this.notificationState === this.notificationTypes.ACCOUNT_QUESTIONS) {
      await this.fetchQuestions();
    }
    if (this.$route.query.expenses) {
      const previousBudgetDate = casfhlowHistory.getPrevBudgetDate(this.budgetDate);
      const initialBudgetDate = this.budgetDate === casfhlowHistory.getCurrentBudgetDate() ? previousBudgetDate : this.budgetDate;
      this.openModal({
        component: ExpenseSummary,
        props: {
          initialBudgetDate,
        },
      });
    }
    if (this.$route.query.midmonth) {
      this.openModal({ component: MidMonthEvent });
    }
    this.doneContentLoading(this.$options.name);
    await Intercom.update(
      this.customerId, this.householdName, {
        cashflow: this.predictedBalanceAtEndOfMonth > 0 ? 'Positive' : 'Negative',
        hasFixedIncome: !!this.cashflowUITree.income.fixedIncome,
        zeroIncomeForecast: _.isEqual(this.cashflowUITree.income.recommendedOrExpectedAmount, 0),
      },
    );
  },
  computed: {
    ...mapGetters('session', ['householdName']),
    ...mapState('session', ['customerId']),
    ...mapGetters('cashflowView', ['budgetUpdatedAt', 'isPresentingCurrentMonth', 'isPresentingLastMonth', 'predictedBalanceAtEndOfMonth']),
    ...mapState('cashflowView', ['cashflowUITree']),
    ...mapState('applicationState', ['notificationState', 'cashflowState']),
    ...mapGetters('applicationState', ['notificationTypes', 'isActiveNotification', 'cashflowTypes']),
    ...mapGetters('insights', ['filteredInsights']),
    showNotification() {
      return this.isActiveNotification && (this.isPresentingCurrentMonth || this.isPresentingLastMonth);
    },
    showCarousel() {
      return !this.blocked && this.isPresentingCurrentMonth && this.anyCarouselCards;
    },
    anyCarouselCards() {
      return this.filteredInsights.length > 0;
    },
    blocked() {
      return this.cashflowState === this.cashflowTypes.BLOCKED;
    },
    budgetDate() {
      const currentBudgetDate = casfhlowHistory.getCurrentBudgetDate();
      return this.$route.query.msg === 'eom' ? casfhlowHistory.getPrevBudgetDate(currentBudgetDate) : currentBudgetDate;
    },
  },
  methods: {
    ...mapActions('contentLoader', ['startContentLoading', 'doneContentLoading']),
    ...mapActions('applicationState', ['initApplicationState']),
    ...mapActions('cashflowView', ['initBudgets']),
    ...mapActions('accountInvestigationQuestions', ['fetchQuestions']),
    ...mapActions('mergeInvestigationQuestions', ['fetchMergeQuestions']),
    ...mapActions('insights', ['initInsights']),
    ...mapActions('modalRootStore', ['openModal']),
  },
};
</script>

<style lang="scss" scoped>
@import '../../../../../../node_modules/base-ui/src/scss/riseup-colors';
@import '../../../../../scss/mixins';
@import '../../../../../scss/global-variables';

.cashflow-app {
  ::v-deep {
    .offer-timer {
      margin-right: 30px;
      margin-left: 30px;
    }

  }

  .aboveCarousel {
    background: $riseup_gray_-1;
  }

  font-family: SimplerPro;

  .bars-container {
    position: sticky;
    top: $app_header_height;
    z-index: 200;
  }

  .notification-wrapper {
    @include responsive-padding;
    padding-top: 30px;
    padding-bottom: 30px;
    background-color: $riseup_gray_-1;
  }

  .cashflow-container {
    padding-top: 30px;
    background-color: $riseup_white;
  }
}
</style>
